/* Essencraft PDP motion (spec v1.6 §6.2). Vanilla JS + IntersectionObserver.
   All content is visible without JS; motion is additive only.
   Hard rules honored here:
   - no selector may match the Recharge app block, product form, add-to-cart,
     payment or cart controls (all hooks are ec- data attributes on new
     visual sections only);
   - reduced-motion / save-data disables everything;
   - no scroll hijacking, no autoplay carousels. */
(function () {
  'use strict';

  var mediaReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var saveData = navigator.connection && navigator.connection.saveData;
  var motionAllowed = !mediaReduced && !saveData;

  function track(name, payload) {
    if (window.ecTrack) window.ecTrack(name, payload);
  }

  /* Root flag: CSS applies pre-reveal transforms only under .ec-motion-on,
     so content never hides when JS or motion is unavailable. */
  if (motionAllowed) {
    document.documentElement.classList.add('ec-motion-on');
  }

  function initReveals() {
    if (!motionAllowed || !('IntersectionObserver' in window)) return;
    var els = document.querySelectorAll('[data-ec-motion="reveal"], [data-ec-reveal]');
    if (!els.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.15 });
    els.forEach(function (el) { io.observe(el); });
  }

  function initAnnotations() {
    if (!('IntersectionObserver' in window)) return;
    document.querySelectorAll('[data-ec-annotations]').forEach(function (group) {
      var items = group.querySelectorAll('[data-ec-annotation]');
      items.forEach(function (item, i) {
        item.style.transitionDelay = motionAllowed ? (i * 50) + 'ms' : '0ms';
      });
      if (!motionAllowed) {
        group.classList.add('is-in');
        return;
      }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            group.classList.add('is-in');
            io.unobserve(group);
          }
        });
      }, { threshold: 0.25 });
      io.observe(group);
    });
  }

  /* Desktop macro parallax: max 16px total travel, disabled under 992px. */
  function initParallax() {
    if (!motionAllowed || window.innerWidth < 992 || !('IntersectionObserver' in window)) return;
    var els = document.querySelectorAll('[data-ec-motion="parallax"] .ec-slot__media');
    if (!els.length) return;
    var active = [];
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var idx = active.indexOf(entry.target);
        if (entry.isIntersecting && idx === -1) active.push(entry.target);
        if (!entry.isIntersecting && idx !== -1) active.splice(idx, 1);
      });
    });
    els.forEach(function (el) { io.observe(el); });

    var ticking = false;
    function update() {
      ticking = false;
      active.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        var progress = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
        var y = Math.max(-8, Math.min(8, -progress * 16));
        el.style.transform = 'translateY(' + y.toFixed(1) + 'px) scale(1.03)';
      });
    }
    window.addEventListener('scroll', function () {
      if (!ticking && active.length) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }, { passive: true });
    update();
  }

  /* Step media: [data-ec-steps] container with [data-ec-step] items and
     [data-ec-step-frame] media frames. The active step's frame crossfades in
     as steps enter view. No pinning, no scroll interception. */
  function initStepMedia() {
    document.querySelectorAll('[data-ec-steps]').forEach(function (container) {
      var steps = container.querySelectorAll('[data-ec-step]');
      var frames = container.querySelectorAll('[data-ec-step-frame]');
      if (!steps.length || !frames.length) return;

      function activate(index) {
        steps.forEach(function (s, i) { s.classList.toggle('is-active', i === index); });
        frames.forEach(function (f, i) { f.classList.toggle('is-active', i === Math.min(index, frames.length - 1)); });
      }
      activate(0);

      if (!motionAllowed || !('IntersectionObserver' in window)) return;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var idx = Array.prototype.indexOf.call(steps, entry.target);
            if (idx > -1) {
              activate(idx);
              track('ec_motion_step_view', { step_index: idx, section_id: container.id || null });
            }
          }
        });
      }, { rootMargin: '-40% 0px -40% 0px' });
      steps.forEach(function (s) { io.observe(s); });
    });
  }

  /* Expectations canvas: manual label rail crossfades frames. */
  function initCanvasToggles() {
    document.querySelectorAll('[data-ec-canvas]').forEach(function (canvas) {
      var tabs = canvas.querySelectorAll('[data-ec-canvas-tab]');
      var frames = canvas.querySelectorAll('[data-ec-canvas-frame]');
      if (!tabs.length || !frames.length) return;
      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () {
          tabs.forEach(function (t, j) {
            t.classList.toggle('is-active', j === i);
            t.setAttribute('aria-selected', j === i ? 'true' : 'false');
          });
          frames.forEach(function (f, j) { f.classList.toggle('is-active', j === i); });
        });
      });
    });
  }

  /* Comparison focus toggle. */
  function initCompareFocus() {
    document.querySelectorAll('[data-ec-compare]').forEach(function (root) {
      var cards = root.querySelectorAll('[data-ec-compare-card]');
      cards.forEach(function (card) {
        ['mouseenter', 'focusin'].forEach(function (evt) {
          card.addEventListener(evt, function () {
            cards.forEach(function (c) { c.classList.toggle('is-focus', c === card); });
          });
        });
      });
      root.addEventListener('mouseleave', function () {
        cards.forEach(function (c) { c.classList.remove('is-focus'); });
      });
    });
  }

  /* Manual carousel: arrows scroll one item; scroll-snap does the rest. */
  function initCarousels() {
    document.querySelectorAll('[data-ec-carousel]').forEach(function (root) {
      var scroller = root.querySelector('[data-ec-carousel-track]');
      if (!scroller) return;
      root.querySelectorAll('[data-ec-carousel-prev], [data-ec-carousel-next]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var item = scroller.firstElementChild;
          if (!item) return;
          var delta = item.getBoundingClientRect().width + 16;
          scroller.scrollBy({
            left: btn.hasAttribute('data-ec-carousel-prev') ? -delta : delta,
            behavior: motionAllowed ? 'smooth' : 'auto'
          });
        });
      });
    });
  }

  /* Pause slot videos offscreen; track first view of each slot. */
  function initSlotObservers() {
    if (!('IntersectionObserver' in window)) return;
    var seen = {};
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var slot = entry.target;
        var video = slot.querySelector('[data-ec-video]');
        if (video && !entry.isIntersecting && !video.paused) video.pause();
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          var id = slot.getAttribute('data-ec-slot');
          if (id && !seen[id]) {
            seen[id] = true;
            track('ec_media_view', { media_slot: id });
          }
        }
      });
    }, { threshold: [0, 0.5] });
    document.querySelectorAll('[data-ec-slot]').forEach(function (slot) { io.observe(slot); });
  }

  function boot() {
    initReveals();
    initAnnotations();
    initParallax();
    initStepMedia();
    initCanvasToggles();
    initCompareFocus();
    initCarousels();
    initSlotObservers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
